import { apiFetch } from "./api.js";
import { fetchGroupDetail } from "./meta-cache.js";
import type { GroupDetail } from "./api-types.js";
import { postLetterReorder } from "./reorder.js";
import { escapeHtml, toast } from "./util.js";
import { downloadWithTimeout, mediaDownloadLimit, photoPreviewSize } from "./download.js";
import { charCounter, overCharLimit, attachmentIssue } from "./compose-limits.js";
import {
  formatPublishAt,
  formatTurnLabel,
  formatDateTime,
  formatWeekdayDateTime,
  formatWeekdayDate,
  isUnscheduled,
  formatWriteBy,
} from "./date-utils.js";
import { t } from "./i18n.js";
import Sortable from "sortablejs";
import { mediaCache } from "./media-cache.js";
import { getPreview, putPreview } from "./media-store.js";
import { resolveLetterItems, type DmMessage, type LetterItem } from "./resolve.js";
import { CustomFile } from "telegram/client/uploads.js";
import { Api } from "telegram";

/** Read a video file's real dimensions and duration in the browser.
 *
 *  Without these, sendFile uploads the file with no DocumentAttributeVideo, so
 *  Telegram never treats it as a proper video: no dimensions, no duration, and
 *  — per GramJS's own docs — no thumbnail. That is what made iPhone recordings
 *  render as a sliver (no w/h to size the element) and then not at all (no
 *  thumb to swap in). Resolves to null if the browser can't decode the file,
 *  in which case we upload without attributes exactly as before. */
async function probeVideo(
  file: File,
): Promise<{ w: number; h: number; seconds: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let settled = false;
    const done = (r: { w: number; h: number; seconds: number } | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(r);
    };
    // Safari decodes .MOV natively, so metadata is usually available even when
    // playback elsewhere would fail. Cap the wait so a stubborn file can't
    // block the send.
    const timer = setTimeout(() => done(null), 5000);
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      clearTimeout(timer);
      const w = v.videoWidth, h = v.videoHeight;
      const seconds = Number.isFinite(v.duration) ? Math.round(v.duration) : 0;
      done(w && h ? { w, h, seconds } : null);
    };
    v.onerror = () => { clearTimeout(timer); done(null); };
    v.src = url;
  });
}
import { NewMessage } from "telegram/events";
import { EditedMessage } from "telegram/events/EditedMessage.js";
import { DeletedMessage } from "telegram/events/DeletedMessage.js";

const STARTED_KEY = "snail_bot_started";

/** Human-readable error text. Telegram caps message *edits* at 48h (delete has
 *  no limit), surfacing a raw MESSAGE_EDIT_TIME_EXPIRED that means nothing to a
 *  writer — translate it and point at the escape hatch (delete + rewrite). */
function friendlyError(verb: string, e: any): string {
  const raw = e?.message || String(e);
  if (/EDIT_TIME_EXPIRED/i.test(raw)) {
    return t("compose.err_edit_expired");
  }
  return `${verb} failed: ${raw}`;
}

/** Open the bot DM once so the peer exists. Sends `/start` a single time; the
 *  new model has no /draft ceremony. */
async function ensureBotStarted(client: any, botPeer: any): Promise<void> {
  if (localStorage.getItem(STARTED_KEY)) return;
  await client.sendMessage(botPeer, { message: "/start" });
  localStorage.setItem(STARTED_KEY, "1");
}

/** Resolve the bot to a FULL entity usable for reading history, not just
 *  sending. getInputEntity(username) yields a peer that can send but whose
 *  access hash isn't in the read cache, so getMessages returns [] (total: 0).
 *  Priming with getDialogs populates the cache; getEntity then resolves fully. */
async function resolveBotPeer(state: any): Promise<any> {
  try {
    return await state.client.getEntity(state.botUsername);
  } catch {
    await state.client.getDialogs({ limit: 100 });
    return await state.client.getEntity(state.botUsername);
  }
}

interface BlockInfo {
  id: number;
  /** Bot-API dm_message_id; populated from letter.items on load. Used for reorder API. */
  dmId: number | null;
  el: HTMLElement;
  isMedia: boolean;
  /** Last value we wrote to / read from Telegram, for change detection. */
  lastValue: string;
  /** Object URL for a locally-previewed image; revoked when the block goes. */
  localImageUrl?: string;
}

/** Render the compose area into `container`. When it's the user's turn the
 *  member edits their OPEN LETTER directly: each existing DM message is one
 *  editable block; new messages are sent (and committed) on the fly.
 *  `detail`, when given, is the caller's already-in-flight fresh /groups/{id}
 *  fetch — turn state must gate on live data, so this is always a network
 *  response, never the metadata cache. */
export async function renderComposeArea(
  container: HTMLElement, state: any, groupId: string,
  detail?: Promise<GroupDetail>
): Promise<void> {
  // Tear down any handler left over from a previous mount before re-rendering.
  if (state._composeCleanup) {
    try { state._composeCleanup(); } catch { /* ignore */ }
    state._composeCleanup = null;
  }

  // The three opening fetches are independent — fire them together instead of
  // serially: group metadata (usually the caller's already-in-flight fetch),
  // the open letter, and the bot entity. The letter and peer are consumed only
  // when the viewer can compose; the no-op .catch keeps an early return from
  // leaving an unhandled rejection (the awaits below still throw).
  const letterPromise = apiFetch(`/groups/${groupId}/letter`);
  letterPromise.catch(() => {});
  const botPeerPromise = resolveBotPeer(state);
  botPeerPromise.catch(() => {});

  const data = await (detail ?? fetchGroupDetail(groupId));
  const me = data.members.find((m: any) => m.is_you);
  const isMyTurn = !!(me && data.current_turn_member_id === me.id);
  const isActive = data.status === "active";
  // Draft anytime: any member of an active group can compose. It's only their
  // live letter when it's their turn; otherwise it's a draft that publishes when
  // their turn comes (the bot parks it at NO_DEADLINE; the slot fills in then).
  const canCompose = isActive && !!me;
  const canAdd = canCompose && !(isMyTurn && !!data.queued_draft?.sealed_at);

  let banner = "";
  if (!isActive) {
    banner = `<div class="compose-banner muted-banner">${t("compose.paused")}</div>`;
  } else if (!isMyTurn) {
    const who = data.current_turn_display_name
      ? t("compose.someone_writing", { who: escapeHtml(data.current_turn_display_name) })
      : t("compose.not_your_turn_yet");
    // Show BOTH the current writer's scheduled publish date and the viewer's
    // own (when known). Each datetime is local; null/NO_DEADLINE falls back to
    // the vague phrasing for that half.
    // Current writer's slot keeps the time (a firm deadline); the viewer's own
    // slot is a soft projection that slides on skips, so show date only.
    const cur =
      data.next_publication_slot && !isUnscheduled(data.next_publication_slot)
        ? formatWeekdayDateTime(new Date(data.next_publication_slot))
        : null;
    const mine =
      data.your_next_slot && !isUnscheduled(data.your_next_slot)
        ? formatWeekdayDate(new Date(data.your_next_slot))
        : null;
    let text: string;
    if (cur) {
      const yours = mine
        ? t("compose.draft_ahead_yours_dated", { when: mine })
        : t("compose.draft_ahead_yours_undated");
      text = t("compose.draft_ahead_current_dated", { who, current: cur }) + yours;
    } else if (mine) {
      text = t("compose.draft_ahead_banner_dated", { who, when: mine });
    } else {
      text = t("compose.draft_ahead_banner", { who });
    }
    banner = `<div class="compose-banner muted-banner">${text}</div>`;
  } else {
    // It's the viewer's turn — an affirmative call to action with their own
    // deadline (they ARE the current writer, so next_publication_slot is theirs).
    const when =
      data.next_publication_slot && !isUnscheduled(data.next_publication_slot)
        ? formatWeekdayDateTime(new Date(data.next_publication_slot))
        : null;
    const qd = data.queued_draft;
    if (qd?.sealed_at) {
      const text = when
        ? t("compose.sealed_banner", { when })
        : t("compose.sealed_banner_undated");
      banner = `<div class="compose-banner turn-banner">${text}
        <a href="#" id="compose-cancel-link" class="compose-skip-link">${t("compose.cancel_letter")}</a></div>`;
    } else {
      const wb = formatWriteBy(qd?.write_by ?? null);
      const base = when ? t("compose.turn_banner_dated", { when }) : t("compose.turn_banner");
      const writeBy = wb
        ? ` ${t("compose.write_by", { when: wb })}
          <a href="#" class="compose-extend-link compose-skip-link" data-days="1">${t("compose.extend_1")}</a>
          <a href="#" class="compose-extend-link compose-skip-link" data-days="3">${t("compose.extend_3")}</a>
          <a href="#" class="compose-extend-link compose-skip-link" data-send="1">${t("compose.extend_send")}</a>`
        : "";
      banner = `<div class="compose-banner turn-banner">${base}${writeBy}
        <a href="#" id="compose-skip-link" class="compose-skip-link">${t("compose.skip_turn")}</a></div>`;
    }
  }

  container.innerHTML = `
    <div class="compose-section">
      <div class="compose-header">
        <span class="compose-label" id="compose-label">${isMyTurn ? t("compose.label.your_turn") : t("compose.label.your_draft")}</span>
      </div>
      ${banner}
      ${canCompose ? `
        <div id="compose-blocks"></div>
        ${canAdd ? `<div class="compose-add">
          <textarea id="compose-add-text" class="compose-editor"
            placeholder="${isMyTurn ? t("compose.add_placeholder") : t("compose.draft_placeholder")}"></textarea>
          <div class="compose-attach">
            <label class="compose-attach-label" for="compose-file">📎 Attach</label>
            <input type="file" id="compose-file" multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt" style="display:none">
            <button class="btn" id="compose-add-btn" disabled>${t("compose.add")}</button>
          </div>
        </div>` : ""}
        <div class="compose-actions">
          <span class="compose-hint" id="compose-status"></span>
          <span class="compose-hint" id="compose-char-count"></span>
        </div>
        <div id="compose-error" class="error-banner" style="display:none"></div>
      ` : ""}
    </div>`;

  if (isMyTurn) {
    container.querySelector("#compose-cancel-link")?.addEventListener("click", async (e) => {
      e.preventDefault();
      if (!confirm(t("compose.cancel_confirm"))) return;
      try {
        await apiFetch(`/groups/${groupId}/letter/cancel`, { method: "POST" });
        toast(t("compose.cancel_done"));
        await renderComposeArea(container, state, groupId);
      } catch (err: any) {
        toast(err.message || t("compose.cancel_failed"));
      }
    });
    container.querySelectorAll<HTMLAnchorElement>(".compose-extend-link").forEach((a) => {
      a.addEventListener("click", async (e) => {
        e.preventDefault();
        const body = a.dataset.send ? { days: null, until_send: true } : { days: Number(a.dataset.days), until_send: false };
        try {
          await apiFetch(`/groups/${groupId}/letter/extend`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const fresh = await apiFetch(`/groups/${groupId}/letter`);
          const wb = formatWriteBy(fresh.write_by);
          toast(wb ? t("compose.extend_done", { when: wb }) : t("compose.saved"));
          await renderComposeArea(container, state, groupId);
        } catch (err: any) {
          toast(err.message || t("compose.extend_failed"));
        }
      });
    });
  }

  if (!canCompose) return;

  const showError = (m: string) => {
    const e = document.getElementById("compose-error")!;
    e.style.display = "block";
    e.textContent = m;
  };
  const hideError = () => {
    const e = document.getElementById("compose-error");
    if (e) e.style.display = "none";
  };
  // A failed reorder gets a retry button: SortableJS already committed the new
  // order to the DOM, so re-posting it is exactly what the user wants.
  const showReorderFailed = () => {
    const e = document.getElementById("compose-error")!;
    e.style.display = "block";
    e.textContent = t("compose.err_reorder_failed") + " ";
    const retry = document.createElement("button");
    retry.className = "btn";
    retry.textContent = t("compose.retry");
    retry.addEventListener("click", () => void postReorder());
    e.appendChild(retry);
  };

  const botPeer = await botPeerPromise;
  await ensureBotStarted(state.client, botPeer);


  const blocksEl = document.getElementById("compose-blocks")!;
  const addText = document.getElementById("compose-add-text") as HTMLTextAreaElement;
  const fileInput = document.getElementById("compose-file") as HTMLInputElement;
  const addBtn = document.getElementById("compose-add-btn") as HTMLButtonElement;
  const statusEl = document.getElementById("compose-status")!;
  const charCount = document.getElementById("compose-char-count")!;

  // id -> block, so live updates can patch/remove by message id.
  const blocks = new Map<number, BlockInfo>();

  // Drag-to-reorder. SortableJS rather than HTML5 DnD: iOS WebKit never fires
  // drag events for touch, so native DnD is desktop-only. Delegated on the
  // container, so rows appended later by addBlock are picked up automatically.
  Sortable.create(blocksEl, {
    handle: ".compose-block-drag",
    animation: 150,
    ghostClass: "dragging",
    onEnd: (evt) => {
      if (evt.oldIndex !== evt.newIndex) void postReorder();
    },
  });

  // Pin THIS correspondence as the bot's active group before any send, so a
  // multi-group user's DM is captured into the right letter (the bot resolves
  // the target from the DB-authoritative active group, not stale state). Awaited
  // before every send — one fast call, always correct, no client bookkeeping.
  async function activate(): Promise<void> {
    await apiFetch(`/groups/${groupId}/activate`, { method: "POST" });
  }

  // Re-POST the current DOM order of blocks to the reorder API. The ordering
  // and error semantics live in postLetterReorder (reorder.ts, unit-tested);
  // this wires in the DOM order, the API calls, and the visible outcomes.
  async function postReorder(): Promise<void> {
    const childMtpIds = Array.from(blocksEl.children).map((child) =>
      parseInt((child as HTMLElement).dataset.id ?? "0", 10)
    );
    await postLetterReorder(
      childMtpIds,
      (mtId) => blocks.get(mtId)?.dmId ?? null,
      {
        fetchLetter: () => apiFetch(`/groups/${groupId}/letter`),
        postOrder: (ids) =>
          apiFetch(`/groups/${groupId}/letter/reorder`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dm_message_ids: ids }),
          }),
        onUnresolved: () => showError(t("compose.err_reorder_unresolved")),
        onFailed: showReorderFailed,
        onSaved: hideError, // clear any lingering failure banner
      }
    );
  }

  // Footer status line. Fired at the top of this function, in parallel with
  // the metadata and bot-peer fetches; usually settled by now.
  const letter = await letterPromise;
  statusEl.textContent = formatPublishAt(letter.publish_at, isMyTurn);

  // Header label now carries the publish slot ("✨ Your turn — publishes …") or
  // "📝 Your draft" when drafting ahead of the turn.
  const labelEl = document.getElementById("compose-label");
  if (labelEl) labelEl.textContent = formatTurnLabel(letter.publish_at, isMyTurn);

  if (isMyTurn) {
    container.querySelector("#compose-skip-link")?.addEventListener("click", async (e) => {
      e.preventDefault();
      if (!confirm(t("compose.skip_confirm"))) return;
      try {
        await apiFetch(`/groups/${groupId}/skip`, { method: "POST" });
        toast(t("compose.skip_done"));
        await renderComposeArea(container, state, groupId);
      } catch (err: any) {
        toast(err.message || t("compose.skip_failed"));
      }
    });
  }

  // --- Block rendering ----------------------------------------------------

  /** Build and wire one editable block for a Telegram message. `localImageUrl`,
   *  when given, is an object URL for an instantly-shown preview of a photo the
   *  user just picked (no Telegram round-trip). `dmId` is the Bot-API message id
   *  used by the reorder API. */
  function addBlock(msg: any, localImageUrl?: string, dmId: number | null = null): void {
    const id: number = msg.id;
    if (blocks.has(id)) return; // de-dupe (e.g. our own just-sent message)

    const cls = classifyMedia(msg.media);
    const isMedia = !!cls;
    // First-sent timestamp, right-aligned in the actions row. msg.date is Unix
    // seconds (GramJS); absent on optimistic local blocks → leave blank.
    const blockDate = msg.date
      ? escapeHtml(formatDateTime(new Date(msg.date * 1000)))
      : "";
    const row = document.createElement("div");
    row.className = "compose-block";
    row.dataset.id = String(id);

    let editor: HTMLTextAreaElement;
    if (isMedia) {
      const label = cls!.kind === "document"
        ? `📎 ${escapeHtml(cls!.name || "file")}`
        : cls!.kind === "video" ? "🎬 Video" : "🖼 Photo";
      const mediaInner = localImageUrl
        ? `<img class="compose-block-img" src="${localImageUrl}" alt="">`
        : label;
      const caption: string = msg.message || "";
      row.innerHTML = `
        <span class="compose-block-drag" title="Drag to reorder">⠿</span>
        <div class="compose-block-media">${mediaInner}</div>
        <textarea class="compose-block-edit" placeholder="${t("compose.caption_placeholder")}"></textarea>
        <div class="compose-block-actions">
          <button class="btn btn-save" disabled>${t("compose.save")}</button>
          <button class="btn btn-cancel" disabled>${t("compose.cancel")}</button>
          <span class="compose-block-status"></span>
          <span class="compose-block-date">${blockDate}</span>
        </div>
        <span class="compose-block-remove" role="button" title="Remove">✕</span>`;
      editor = row.querySelector(".compose-block-edit") as HTMLTextAreaElement;
      editor.value = caption;
    } else {
      const text: string = msg.message || msg.text || "";
      row.innerHTML = `
        <span class="compose-block-drag" title="Drag to reorder">⠿</span>
        <textarea class="compose-block-edit"></textarea>
        <div class="compose-block-actions">
          <button class="btn btn-save" disabled>${t("compose.save")}</button>
          <button class="btn btn-cancel" disabled>${t("compose.cancel")}</button>
          <span class="compose-block-status"></span>
          <span class="compose-block-date">${blockDate}</span>
        </div>
        <span class="compose-block-remove" role="button" title="Remove">✕</span>`;
      editor = row.querySelector(".compose-block-edit") as HTMLTextAreaElement;
      editor.value = text;
    }

    const info: BlockInfo = {
      id, dmId, el: row, isMedia, lastValue: editor.value, localImageUrl,
    };
    blocks.set(id, info);

    // Media blocks: show a clickable thumbnail (click → full size in a new
    // tab). A just-attached image already has a local preview <img>; only wire
    // its click. Existing media (loaded from history) shows a text label until
    // its thumbnail downloads, then swaps the label for the <img>/poster.
    if (isMedia) {
      const mediaBox = row.querySelector(".compose-block-media") as HTMLElement;
      if (localImageUrl) {
        const img = mediaBox.querySelector("img");
        if (img) {
          img.classList.add("clickable");
          img.addEventListener("click", () => void openFullMedia(msg.media));
        }
      } else if (cls!.kind !== "document") {
        void hydrateBlockThumb(mediaBox, msg.media, cls!.kind, id);
      } else {
        // Document: the label itself downloads on click.
        mediaBox.classList.add("clickable");
        mediaBox.addEventListener("click", () => void openFullMedia(msg.media));
      }
    }

    const saveBtn = row.querySelector(".btn-save") as HTMLButtonElement;
    const cancelBtn = row.querySelector(".btn-cancel") as HTMLButtonElement;
    const statusSpan = row.querySelector(".compose-block-status") as HTMLElement;

    const dirty = () => editor.value !== info.lastValue;
    const refreshDirty = () => {
      const d = dirty();
      saveBtn.disabled = !d;
      cancelBtn.disabled = !d;
      if (d) statusSpan.textContent = "";
    };

    // Commit the edit (or delete, when a text block is cleared). Returns true
    // if the block still exists afterward.
    const save = async (): Promise<boolean> => {
      row.classList.remove("changed-in-telegram");
      if (!dirty()) return true;
      const newVal = editor.value;
      try {
        if (!isMedia && newVal.trim() === "") {
          await state.client.deleteMessages(botPeer, [id], { revoke: true });
          removeBlock(id);
          return false;
        }
        await state.client.editMessage(botPeer, { message: id, text: newVal });
        info.lastValue = newVal;
        refreshDirty();
        statusSpan.textContent = t("compose.saved");
        return true;
      } catch (e: any) {
        // >48h: Telegram refuses the edit. Sidestep it — forward the message
        // back into the chat (a fresh clone of the media/text with the edit
        // clock reset), edit the clone, then reorder so the clone takes the old
        // item's slot and the old item is dropped from the letter. Transparent
        // to the user; falls back to the manual hint if any step fails.
        if (/EDIT_TIME_EXPIRED/i.test(e?.message || String(e))) {
          if (await replaceExpired(id, info, newVal)) return false;
        }
        showError(friendlyError("Edit", e));
        return true;
      }
    };

    editor.addEventListener("input", refreshDirty);
    saveBtn.addEventListener("click", () => void save());
    cancelBtn.addEventListener("click", () => {
      editor.value = info.lastValue;
      refreshDirty();
    });

    (row.querySelector(".compose-block-remove") as HTMLElement)
      .addEventListener("click", async () => {
        try {
          await state.client.deleteMessages(botPeer, [id], { revoke: true });
          removeBlock(id);
        } catch (e: any) {
          showError(friendlyError("Delete", e));
        }
      });

    blocksEl.appendChild(row);
  }

  function removeBlock(id: number): void {
    const info = blocks.get(id);
    if (!info) return;
    if (info.localImageUrl) URL.revokeObjectURL(info.localImageUrl);
    info.el.remove();
    blocks.delete(id);
  }

  // Download a preview for an existing media block and swap the text label for a
  // clickable thumbnail. Photo → the full (already compressed) image, no thumb
  // index; video → smallest thumb (index 0) as a poster. Mirrors the feed's
  // hydrateMedia and shares its session media cache (mediaCache, "dm:"-prefixed
  // here — bare DM ids can collide with supergroup ids) so a given photo/video
  // downloads only once per session. Click opens full size. Best-effort: on
  // failure the label stays.
  async function hydrateBlockThumb(
    box: HTMLElement, media: any, kind: MediaKind, msgId: number
  ): Promise<void> {
    const wire = (url: string) => {
      box.innerHTML = `<img class="compose-block-img clickable" src="${url}" alt="">`;
      box.querySelector("img")!.addEventListener(
        "click", () => void openFullMedia(media)
      );
    };
    const cached = mediaCache.get(`dm:${msgId}`);
    if (cached) { wire(cached); return; }
    try {
      // Reload survivor: a preview stored in a past session renders without
      // touching the network.
      const stored = await getPreview(`dm:${msgId}`);
      if (stored) {
        const url = URL.createObjectURL(stored);
        mediaCache.set(`dm:${msgId}`, url);
        wire(url);
        return;
      }
      // image-doc (HEIC etc.): browser can't decode the original, so render the
      // largest embedded JPEG thumb. Photo: smallest size >= 800px. Video:
      // smallest poster.
      const previewSize = kind === "photo" ? photoPreviewSize(media) : undefined;
      const opts =
        kind === "photo" ? (previewSize ? { thumb: previewSize } : {})
        : kind === "image-doc" ? { thumb: largestThumbIndex(media) }
        : { thumb: 0 };
      const buf = await mediaDownloadLimit(() =>
        downloadWithTimeout(state.client, media, opts, undefined, (late) => {
          if (mediaCache.has(`dm:${msgId}`)) return;
          const blob = new Blob([late], { type: "image/jpeg" });
          mediaCache.set(`dm:${msgId}`, URL.createObjectURL(blob));
          putPreview(`dm:${msgId}`, blob);
        })
      );
      if (!buf) return;
      const blob = new Blob([buf], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      mediaCache.set(`dm:${msgId}`, url);
      putPreview(`dm:${msgId}`, blob);
      wire(url);
    } catch (e) {
      console.error("compose thumb failed", e);
    }
  }

  // Download full media and open it in a new tab. An untyped blob opens as
  // text/octet-stream (Chrome renders it as raw text instead of the image),
  // so give it a real mime type — photos are always JPEG-compressed by
  // Telegram; documents keep their own mime type when known.
  async function openFullMedia(media: any): Promise<void> {
    try {
      const buf = await state.client.downloadMedia(media, {});
      if (!buf) return;
      const cls = classifyMedia(media);
      const mime =
        cls?.kind === "photo" ? "image/jpeg"
        : cls?.kind === "video" ? "video/mp4"
        : media?.document?.mimeType || "application/octet-stream";
      window.open(URL.createObjectURL(new Blob([buf], { type: mime })), "_blank");
    } catch (e) {
      console.error("compose media download failed", e);
    }
  }

  // 48h-edit sidestep: forward the too-old message into the same chat (dropAuthor
  // = content clone, media by reference, fresh edit clock), apply the edit to the
  // clone, then reorder the letter so the clone takes the old item's slot with
  // the old id omitted — set_letter_order drops the unlisted old item, so the
  // reorder both repositions the clone and removes the stale item (no separate
  // delete, no swap endpoint). The old DM message itself is left in Telegram
  // (we don't destroy the user's messages); it's simply no longer in the letter.
  // Returns true on success. All Telegram ops run on the user's own MTProto
  // session; the server only updates the DB item order.
  async function replaceExpired(
    oldMtpId: number, oldInfo: BlockInfo, newVal: string
  ): Promise<boolean> {
    const oldDmId = oldInfo.dmId;
    if (oldDmId == null) {
      console.error("[replaceExpired] no oldDmId for block", oldMtpId);
      return false;
    }
    try {
      // 1) Forward the too-old message into the chat (dropAuthor = content clone,
      //    media by reference, fresh edit clock), then edit the clone to the new
      //    text/caption. The clone is the replacement. activate() first so the
      //    bot captures the forwarded DM into THIS correspondence.
      await activate();
      const fwd = await state.client.forwardMessages(botPeer, {
        messages: [oldMtpId], fromPeer: botPeer, dropAuthor: true,
      });
      // forwardMessages returns one entry per source chat, each itself an
      // array of forwarded Message objects (GramJS's per-request-id shape) —
      // unwrap both levels, not just one, or `fresh` is an array and every
      // downstream `fresh.id`/`fresh.message` read comes back undefined.
      const firstChat = Array.isArray(fwd) ? fwd[0] : fwd;
      const fresh = Array.isArray(firstChat) ? firstChat[0] : firstChat;
      if (!fresh) { console.error("[replaceExpired] forward returned nothing"); return false; }
      if (newVal !== (fresh.message ?? fresh.text ?? "")) {
        await state.client.editMessage(botPeer, { message: fresh.id, text: newVal })
          .catch((e: any) => console.error("[replaceExpired] edit clone:", e?.message || e));
      }

      // 2) Let the bot receive+append the forwarded DM, then read the fresh
      //    letter and resolve the new message's Bot-API id. Poll: the bot's
      //    capture is async, and slower still on a reconnecting MTProto
      //    connection (observed: repeated "Connection closed while receiving
      //    data" cycles), so this budgets ~30s and tolerates a flaky attempt
      //    instead of letting one transient error abort the whole wait.
      let newDmId: number | null = null;
      for (let attempt = 0; attempt < 30 && newDmId == null; attempt++) {
        await new Promise((r) => setTimeout(r, 1000));
        try {
          const after = await apiFetch(`/groups/${groupId}/letter`);
          const msgs = await state.client.getMessages(botPeer, { limit: 200 });
          const dmMsgs: DmMessage[] = msgs
            .filter((m: any) => m)
            .map((m: any) => ({ id: m.id, date: m.date ?? 0, out: !!m.out }));
          const { toFreeze } = resolveLetterItems(after.items || [], dmMsgs);
          if (Object.keys(toFreeze).length) {
            await apiFetch(`/groups/${groupId}/letter/resolve`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ resolved: toFreeze }),
            }).catch(() => { /* best-effort */ });
          }
          const mtpToDm = new Map<number, number>();
          for (const it of (after.items || [])) {
            if (it.mtproto_id != null) mtpToDm.set(it.mtproto_id, it.dm_message_id);
          }
          newDmId = mtpToDm.get(fresh.id) ?? toFreeze[String(fresh.id)] ?? null;
        } catch (e: any) {
          // Transient (e.g. connection mid-reconnect) — keep polling rather
          // than aborting the whole replace on one blip.
          console.warn("[replaceExpired] poll attempt failed, retrying:", e?.message || e);
        }
      }
      if (newDmId == null) {
        // Couldn't map the clone to its Bot-API id in time. We can't run the
        // reorder, so the old item would survive — leaving BOTH it and the clone
        // in the letter (a duplicate at publish). Undo the clone we just made and
        // hard-fail to the manual hint instead.
        console.error("[replaceExpired] could not resolve new dm_id for", fresh.id);
        await state.client.deleteMessages(botPeer, [fresh.id], { revoke: true })
          .catch(() => { /* best-effort cleanup */ });
        return false;
      }

      // 3) Reorder to put the new id in the old's slot and OMIT the old id —
      //    set_letter_order drops unlisted items, so this both repositions the
      //    replacement and deletes the stale item in one call (no swap endpoint,
      //    no separate delete, no real-time race).
      const fresh2 = await apiFetch(`/groups/${groupId}/letter`);
      const order: number[] = [];
      for (const it of (fresh2.items || [])) {
        const dm = it.dm_message_id;
        if (dm === oldDmId) order.push(newDmId);        // replacement takes the slot
        else if (dm === newDmId) continue;              // skip its end-position copy
        else order.push(dm);                            // everything else unchanged
      }
      await apiFetch(`/groups/${groupId}/letter/reorder`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dm_message_ids: order }),
      });
      // 4) Reload — reliable, reflects the dropped old + repositioned new.
      await renderComposeArea(container, state, groupId);
      return true;
    } catch (e: any) {
      console.error("[replaceExpired] failed:", e?.message || e, e);
      return false;
    }
  }

  // --- Load existing letter messages --------------------------------------

  const items: LetterItem[] = letter.items || [];
  if (items.length) {
    try {
      // Fetch a window of DM history once. getMessages({ids}) is unreliable in
      // private chats (returns null for some IDs, known GramJS issue since
      // 3489b7a); plain {limit} is the safe path.
      const msgs = await state.client.getMessages(botPeer, { limit: 500 });
      const byId = new Map<number, any>();
      const dmMessages: DmMessage[] = [];
      for (const m of msgs) {
        if (!m) continue;
        byId.set(m.id, m);
        dmMessages.push({ id: m.id, date: m.date ?? 0, out: !!m.out });
      }

      // Resolve items to their MTProto messages (date + send-order; see
      // resolve.ts and CLAUDE.md for why id arithmetic can't work). Pure +
      // unit-tested in resolve.test.ts.
      const { matched, toFreeze } = resolveLetterItems(items, dmMessages);
      for (const { item, mtproto_id } of matched) {
        const full = byId.get(mtproto_id);
        // Carry the item's OWN dm_message_id onto the block. (Don't look it up
        // via mtproto_id in a frozen-only map — an item resolved this session but
        // not yet frozen wouldn't be there, leaving dmId=null, which makes
        // postReorder silently drop it and the server delete it on reorder.)
        if (full) addBlock(full, undefined, item.dm_message_id);
      }

      if (Object.keys(toFreeze).length) {
        await apiFetch(`/groups/${groupId}/letter/resolve`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resolved: toFreeze }),
        }).catch(() => { /* best-effort freeze; retries next load */ });
      }
    } catch (e: any) {
      showError(t("compose.err_load", { detail: e?.message || e }));
    }
  }

  // --- Adding new content --------------------------------------------------

  if (canAdd) {
    const syncAddState = () => {
      const len = addText.value.length;
      charCount.textContent = charCounter(len) ?? "";
      charCount.classList.toggle("over-limit", overCharLimit(len));
      addBtn.disabled = addText.value.trim() === "" || overCharLimit(len);
    };
    addText.addEventListener("input", syncAddState);

    addBtn.addEventListener("click", async () => {
      const text = addText.value.trim();
      if (text === "") return;
      // Slash-commands are claimed by the bot as commands, not appended to the
      // letter, and the bot's reply lands in the Telegram DM where the web user
      // isn't looking. Don't send them at all — steer the user to Telegram and
      // leave the text in the box so they can copy it.
      if (text.startsWith("/")) {
        showError(t("compose.err_command", { cmd: text.split(/\s+/)[0] }));
        return;
      }
      if (overCharLimit(text.length)) {
        showError(t("compose.err_too_long"));
        return;
      }
      addBtn.disabled = true;
      try {
        await activate();
        const sent = await state.client.sendMessage(botPeer, { message: text });
        addText.value = "";
        if (sent) addBlock(sent); // de-dupe guards against the live NewMessage echo
      } catch (e: any) {
        showError(t("compose.err_send", { detail: e?.message || e }));
      } finally {
        syncAddState();
      }
    });

    fileInput.addEventListener("change", async () => {
      const files = Array.from(fileInput.files || []);
      fileInput.value = "";
      for (const file of files) {
        const issue = attachmentIssue(file.size);
        if (issue === "too_big") {
          showError(t("compose.err_file_too_big", { name: file.name }));
          continue;
        }
        if (issue === "slow") toast(t("compose.slow_upload"));

        const isImage = file.type.startsWith("image/");
        const forceDocument = !(isImage || file.type.startsWith("video/"));

        // Optimistic block: for an image, show it instantly from the local file
        // (no upload round-trip) with an t("compose.uploading") status. Keyed by a
        // temporary negative id so it stays out of the live NewMessage echo path
        // (real ids are positive); rebound to the real id once sendFile returns.
        // Optimistic preview: for an image, show it instantly from the local
        // file (no upload round-trip) with an t("compose.uploading") status, then swap in
        // the real Telegram-backed block once sendFile returns. The preview is a
        // bare DOM row (not in `blocks`), so it never collides with the live
        // NewMessage echo, which keys on the real positive id.
        let preview: HTMLElement | null = null;
        let previewUrl: string | undefined;
        if (isImage) {
          previewUrl = URL.createObjectURL(file);
          preview = document.createElement("div");
          preview.className = "compose-block";
          preview.innerHTML = `
            <div class="compose-block-media">
              <img class="compose-block-img" src="${previewUrl}" alt=""></div>
            <div class="compose-block-actions">
              <span class="compose-block-status">uploading…</span>
            </div>`;
          blocksEl.appendChild(preview);
        }

        try {
          // GramJS's _fileToMedia can't turn a raw browser File into media (it
          // falls through to getInputMedia and throws "Cannot use [object File]
          // as file"). Wrap the File's bytes in a CustomFile, which the upload
          // path handles via its buffer.
          const buf = Buffer.from(await file.arrayBuffer());
          const wrapped = new CustomFile(file.name, buf.length, "", buf);
          await activate();
          // Videos must carry DocumentAttributeVideo or Telegram won't generate a
          // thumbnail or record dimensions — see probeVideo. supportsStreaming
          // also asks Telegram to remux where it can, which is what gives an
          // iPhone .MOV a chance of playing in a browser at all.
          const isVideo = file.type.startsWith("video/");
          const probed = isVideo ? await probeVideo(file) : null;
          const attributes = probed
            ? [
                new Api.DocumentAttributeVideo({
                  duration: probed.seconds,
                  w: probed.w,
                  h: probed.h,
                  supportsStreaming: true,
                }),
              ]
            : undefined;
          const sent = await state.client.sendFile(
            botPeer,
            { file: wrapped, caption: "", forceDocument, attributes },
          );
          if (preview) preview.remove();
          // Reuse addBlock for all wiring; pass the local url so the image stays
          // visible without a download (revoked when the block is removed).
          if (sent) addBlock(sent, previewUrl);
          else if (previewUrl) URL.revokeObjectURL(previewUrl);
        } catch (e: any) {
          if (preview) {
            const s = preview.querySelector(".compose-block-status") as HTMLElement;
            s.textContent = t("compose.upload_failed");
            preview.classList.add("upload-failed");
          }
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          showError(t("compose.err_upload", { detail: e?.message || e }));
        }
      }
    });
  }

  // --- Live two-way sync ---------------------------------------------------

  const onNew = (event: any) => {
    const msg = event.message;
    if (!msg) return;
    // Only messages this account sent into the bot DM are letter content.
    if (!msg.out) return;
    // Slash-commands are NOT letter content — the bot never appends them
    // server-side (it claims them as commands), so a block here would be a
    // phantom that never publishes. Mirror the bot's gate.
    const text: string = msg.message ?? msg.text ?? "";
    if (text.startsWith("/")) return;
    if (blocks.has(msg.id)) return; // already added locally on send
    addBlock(msg);
  };

  const onEdited = (event: any) => {
    const msg = event.message;
    if (!msg) return;
    const info = blocks.get(msg.id);
    if (!info) return;
    const editor = info.el.querySelector(".compose-block-edit") as HTMLTextAreaElement;
    const incoming = msg.message ?? msg.text ?? "";
    if (document.activeElement === editor && editor.value !== incoming) {
      // User is mid-edit on this block; don't clobber. Flag it — they can Save
      // (their version wins) or Cancel/reload to take the Telegram version.
      info.el.classList.add("changed-in-telegram");
      return;
    }
    editor.value = incoming;
    info.lastValue = incoming;
    // Reset this block's Save/Cancel to clean after a remote update.
    const saveBtn = info.el.querySelector(".btn-save") as HTMLButtonElement | null;
    const cancelBtn = info.el.querySelector(".btn-cancel") as HTMLButtonElement | null;
    if (saveBtn) saveBtn.disabled = true;
    if (cancelBtn) cancelBtn.disabled = true;
  };

  const onDeleted = (event: any) => {
    for (const id of event.deletedIds || []) {
      if (blocks.has(id)) removeBlock(id);
    }
  };

  // Listen on the bot DM CHAT (not fromUsers — your own outgoing message is
  // "from you", so a fromUsers:[bot] filter would never match it). onNew keeps
  // only outgoing messages (msg.out) so the bot's own replies don't become
  // blocks. DeletedMessage carries no reliable peer in private chats, so we
  // filter by known block ids in onDeleted.
  state.client.addEventHandler(onNew, new NewMessage({ chats: [state.botUsername] }));
  state.client.addEventHandler(onEdited, new EditedMessage({ chats: [state.botUsername] }));
  state.client.addEventHandler(onDeleted, new DeletedMessage({}));

  state._composeCleanup = () => {
    state.client.removeEventHandler(onNew, new NewMessage({}));
    state.client.removeEventHandler(onEdited, new EditedMessage({}));
    state.client.removeEventHandler(onDeleted, new DeletedMessage({}));
  };
}

type MediaKind = "photo" | "video" | "image-doc" | "document";

/** Classify a GramJS message's media, or null if none/unsupported. */
function classifyMedia(media: any): { kind: MediaKind; name?: string } | null {
  if (!media) return null;
  if (media.className === "MessageMediaPhoto") return { kind: "photo" };
  if (media.className === "MessageMediaDocument") {
    const doc = media.document;
    const attrs: any[] = doc?.attributes || [];
    const isVideo =
      attrs.some((a) => a.className === "DocumentAttributeVideo") ||
      String(doc?.mimeType || "").startsWith("video/");
    if (isVideo) return { kind: "video" };
    const fileAttr = attrs.find((a) => a.className === "DocumentAttributeFilename");
    const name = fileAttr?.fileName || "file";
    // iPhone HEIC and other images sent as files arrive as documents; show their
    // JPEG thumbnail inline rather than as a paperclip. See groups.ts.
    if (String(doc?.mimeType || "").startsWith("image/"))
      return { kind: "image-doc", name };
    return { kind: "document", name };
  }
  return null;
}

/** Index of the largest entry in a document's `thumbs` list, for downloadMedia's
 *  `thumb` option. Ranks by pixel area, then byte size. Mirrors groups.ts. */
function largestThumbIndex(media: any): number {
  const thumbs: any[] = media?.document?.thumbs || [];
  if (thumbs.length <= 1) return 0;
  const area = (t: any) =>
    (t?.w || 0) * (t?.h || 0) || t?.size || (t?.bytes?.length ?? 0);
  let best = 0;
  for (let i = 1; i < thumbs.length; i++) {
    if (area(thumbs[i]) > area(thumbs[best])) best = i;
  }
  return best;
}
